import { useEffect } from 'react'
import { debugLog } from '../utils/debug'

/**
 * Hook to handle deep linking between URL and Scene state.
 * Syncs URL path to Scene Zone and vice versa.
 * 
 * @param {Object} sceneManager - The SceneManager instance
 * @param {Object} cameraManager - The CameraManager instance
 * @param {boolean} managersReady - Whether managers are initialized
 */
export function useDeepLinking(sceneManager, cameraManager, managersReady, isLoaded)
{
    useEffect(() =>
    {
        // Check if managers are ready and app is loaded (screen removed/faded out)
        if (!managersReady || !sceneManager || !cameraManager || !isLoaded) return

        // 1. URL -> App Sync (Initial + Popstate)
        const handleRoute = (event) =>
        {
            // If we have state from a pop event, use it for direct/faster lookup
            debugLog('[useDeepLinking] (event.state):', event)
            if (event && event.state && typeof event.state.index === 'number')
            {
                debugLog('[useDeepLinking] Restoring from history state:', event.state)
                cameraManager.goToSceneZoneByIndex(event.state.index - 1, false)
                return
            }

            let path = window.location.pathname.substring(1)
            if (path.endsWith('/')) path = path.slice(0, -1)
            path = decodeURIComponent(path).toLowerCase()

            debugLog('[useDeepLinking] Handling route:', path)

            if (path && path.length > 0)
            {
                const zone = sceneManager.getSceneZone(path)
                debugLog('[useDeepLinking] Found zone:', zone ? zone.name : 'null')

                if (zone)
                {
                    cameraManager.goToSceneZoneByName(zone.name, false)
                }
                else
                {
                    // If zone not found, maybe default to first?
                    // Existing logic in Context defaulted to index 0:
                    cameraManager.goToSceneZoneByIndex(0, false)
                }
            } else
            {
                cameraManager.goToSceneZoneByIndex(0, false)
            }
        }

        // Handle initial route with readiness check
        const attemptInitialNavigation = () =>
        {
            // If the camera manager has a scroll object, we must ensure it's ready
            if (cameraManager.scroll)
            {
                // If element is missing, wait
                if (!cameraManager.scroll.el)
                {
                    setTimeout(attemptInitialNavigation, 50)
                    return
                }

                // If element matches criteria for "not scrollable yet" (height mismatch or zero)
                // We use a safe check: if scrollHeight exists but isn't larger than clientHeight,
                // it might mean content hasn't populated/expanded yet.
                const { scrollHeight, clientHeight } = cameraManager.scroll.el
                if (scrollHeight > 0 && scrollHeight <= clientHeight)
                {
                    setTimeout(attemptInitialNavigation, 50)
                    return
                }
            }

            // Ready to handle
            handleRoute()
        }

        // Start the check
        const t = setTimeout(attemptInitialNavigation, 100)

        window.addEventListener('popstate', handleRoute)

        // 2. App -> URL Sync (Subscribe to changes)
        // Leverage XYZ.subscribe (CameraManager.subscribe) to manage current waypoint
        const unsubscribe = cameraManager.subscribe((waypoint) =>
        {
            if (!waypoint) return

            // Get zone from manager to get the name
            const zone = sceneManager.getSceneZoneByIndex(waypoint.index)
            if (zone)
            {
                const zoneName = zone.name.toLowerCase()

                // Get current URL path
                let currentPath = window.location.pathname.substring(1)
                if (currentPath.endsWith('/')) currentPath = currentPath.slice(0, -1)
                currentPath = decodeURIComponent(currentPath).toLowerCase()

                // Only push state if different to avoid duplicate history and loops
                if (zoneName !== currentPath)
                {
                    const data = {
                        index: waypoint.index,
                        name: zoneName
                    }
                    // Push state with metadata (Retain history)
                    window.history.pushState(data, '', `/${zoneName}`)
                }
            }
        })

        return () =>
        {
            window.removeEventListener('popstate', handleRoute)
            if (unsubscribe) unsubscribe()
        }
    }, [ managersReady, sceneManager, cameraManager, isLoaded ])
}
